// CustomTabs.tsx

import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { TabView, TabPanel } from 'primereact/tabview';

// Updated Tab interface with unique id
interface Tab {
  id: string; // Unique identifier for each tab
  header: string;
  content: React.ReactNode;
}

// Updated CustomTabsProps with readonly properties
interface CustomTabsProps {
  readonly tabs: Tab[];
  readonly tabViewClassName?: string; // Optional prop for custom class name
  readonly activeIndex?: number; // Optional initial/controlled active tab index
  readonly onTabChange?: (index: number) => void; // Optional callback when tab changes
}

export interface CustomTabsHandle {
  /**
   * Move keyboard focus to a tab's header. Only the active panel is mounted, so a control that switches
   * tabs from inside a panel unmounts itself; focusing the new tab's header keeps focus on the page.
   */
  focusTab: (index: number) => void;
}

const CustomTabs = forwardRef<CustomTabsHandle, CustomTabsProps>(({
  tabs,
  tabViewClassName,
  activeIndex: controlledActiveIndex,
  onTabChange,
}, ref) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(ref, () => ({
    focusTab: (index: number) => {
      const headers = containerRef.current?.querySelectorAll<HTMLElement>('[role="tab"]');
      headers?.[index]?.focus();
    },
  }), []);

  const [internalActiveIndex, setInternalActiveIndex] = useState(controlledActiveIndex ?? 0);

  // Determine if we're in controlled mode (parent manages state via onTabChange)
  const isControlled = onTabChange !== undefined;
  const activeIndex = isControlled ? (controlledActiveIndex ?? 0) : internalActiveIndex;

  // Update internal index when controlled prop changes (for uncontrolled mode with initial value)
  useEffect(() => {
    if (!isControlled && controlledActiveIndex !== undefined) {
      setInternalActiveIndex(controlledActiveIndex);
    }
  }, [controlledActiveIndex, isControlled]);

  const handleTabChange = (index: number) => {
    if (isControlled) {
      // Controlled mode - notify parent
      onTabChange(index);
    } else {
      // Uncontrolled mode - manage internally
      setInternalActiveIndex(index);
    }
  };

  return (
    <div className="card" ref={containerRef}>
      <TabView
        className={tabViewClassName}
        activeIndex={activeIndex}
        onTabChange={(e) => handleTabChange(e.index)}
      >
        {tabs.map((tab) => (
          // The tab id goes on the header as `data-testid`, not `id`: PrimeReact owns the header's
          // `id`, which the panel's `aria-labelledby` points at.
          <TabPanel key={tab.id} header={tab.header} pt={{ headerAction: { 'data-testid': tab.id } as React.HTMLAttributes<HTMLAnchorElement> }}>
            {tab.content}
          </TabPanel>
        ))}
      </TabView>
    </div>
  );
});

CustomTabs.displayName = 'CustomTabs';

export default CustomTabs;
